import { PhoneIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'

import { useContactInfo } from '@/api/models/common/hooks/useContactInfo'
import { useDeleteRequest } from '@/api/models/requests/hooks/useDeleteRequest'
import {
  RequestStatusAlias,
  RequestTypeAlias
} from '@/api/models/requests/lib/requests.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import ArrowBack from '@/assets/img/ArrowBack.png'
import { PageType } from '@/components/entities/requests/model/types'
import { AppButton } from '@/components/shared/button'
import { AppModal } from '@/components/shared/modal'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatPhoneNumber } from '@/utils/number'

type Props = {
  pageType: PageType
  requestStatus: RequestStatusAlias
  requestNumber: number
  visible: boolean
  onClose: () => void
  onCancelSuccess: () => void
  requestType: RequestTypeAlias
  requestId: string
}

export const CancelRequestModal = ({
  pageType,
  requestStatus,
  requestNumber,
  visible,
  onClose,
  onCancelSuccess,
  requestType,
  requestId
}: Props) => {
  const { reachGoal } = useYandexMetrika()
  const { data: contactInfo } = useContactInfo()
  const { mutateAsync: deleteRequest } = useDeleteRequest()

  const call = () => {
    reachGoal(
      requestType === RequestTypeAlias.CommonRequest
        ? YandexMetrikaEvent.NEW_REQUEST_CANCEL_CALL
        : YandexMetrikaEvent.PASSES_CANCEL_CALL
    )
    window.location.href = `tel:${contactInfo?.contactPhone}`
  }

  const isRequestClosed = [
    RequestStatusAlias.Complete,
    RequestStatusAlias.Reject
  ].includes(requestStatus)

  const isRequestCancellable = [
    RequestTypeAlias.CourierPass,
    RequestTypeAlias.GuestPass,
    RequestTypeAlias.GuestPassAuto,
    RequestTypeAlias.PropertiesPass
  ].includes(requestType)

  const pageTypeText = pageType === PageType.Passes ? 'пропуск' : 'обращение'

  const onCancel = async () => {
    await deleteRequest(requestId)
    reachGoal(YandexMetrikaEvent.PASSES_CANCEL)
    onClose()
    onCancelSuccess()
  }

  return (
    <AppModal
      overlayClassName="!bg-black/50"
      className="mx-16 w-[460px] sm:!h-auto sm:!rounded-16"
      headerWrapperClassName="!pl-32 !pr-20 !pb-12 sm:!p-16 sm:!pb-24"
      closeButtonClassName="!h-40 !w-40 shrink-0 sm:p-4"
      contentClassName="pb-32 !mt-0 gap-y-20 !overflow-y-auto sm:px-24 sm:pb-24"
      header={
        isRequestClosed
          ? `Невозможно отменить ${pageTypeText}`
          : `Отменить ${pageTypeText}`
      }
      opened={visible}
      onClose={onClose}
    >
      {isRequestClosed ? (
        <div className="flex flex-col items-center gap-y-20 text-center">
          <Image
            src="/images/shared/Blocked.png"
            width={140}
            height={140}
            alt="blocked"
            className="rotate-12"
          />
          <p className="w-[340px] text-grey-70 text-20 xxs:text-18">
            Отмена {pageType === PageType.Passes ? 'пропуска' : 'обращения'}{' '}
            невозможна в текущем статусе
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center">
          <Image src={ArrowBack.src} alt="img" width={140} height={140} />
          {isRequestCancellable ? (
            <p
              className={cn(
                isRequestCancellable
                  ? 'w-[370px] font-bold text-grey-100'
                  : 'text-grey-70',
                'pb-12 pt-20 text-20 sm:text-18'
              )}
            >
              Вы уверены, что хотите отменить {pageTypeText}?
            </p>
          ) : (
            <>
              <p className="pb-12 pt-20 font-bold text-20 sm:text-16">
                {pageTypeText[0].toUpperCase() + pageTypeText.slice(1)} №{' '}
                {requestNumber}
              </p>
              <p className="text-grey-70 text-20 sm:text-16">
                Чтобы отменить {pageTypeText} вам нужно связаться со Службой
                комфорта Sminex
              </p>
            </>
          )}
        </div>
      )}
      <div className="flex flex-col gap-12">
        {!isRequestClosed && !isRequestCancellable && (
          <AppButton
            variant="secondary"
            fluid
            className="!h-56 gap-8"
            onClick={call}
          >
            <div className="flex h-32 w-32 items-center justify-center gap-8 rounded-full bg-bronze-40">
              <PhoneIcon className="h-18 w-18" />
            </div>
            {formatPhoneNumber(contactInfo?.contactPhone)}
          </AppButton>
        )}
        {!isRequestClosed && isRequestCancellable && (
          <AppButton variant="secondary" fluid onClick={onCancel}>
            Да, отменить
          </AppButton>
        )}

        <AppButton
          variant={isRequestClosed ? 'secondary' : 'backless'}
          className="!h-56"
          fluid
          onClick={onClose}
        >
          {isRequestClosed ? 'Понятно' : 'Оставить'}
        </AppButton>
      </div>
    </AppModal>
  )
}
